import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  SxProps,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientNavbar from "@/components/general/Navbars/ClientNavbar";
import BackPageButton, { backButtonContainer } from "@/components/general/buttons/BackPageButton";
import { PrimaryButton } from "@/components/general/buttons/PrimaryButton";
import { sharedStyles } from "@/styles/shared";
import { priceFormatter } from "@/utils/priceFormatter";
import orderApi from "@/services/api/Order";
import dayjs from "dayjs";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const PAYMENT_LABELS: Record<string, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Cartão de crédito",
  DEBIT_CARD: "Cartão de débito",
  CREDIT_CARD_EARLY_BUYER: "Cartão de crédito",
  BANK_SLIP: "Boleto bancário",
};

const STATUS_CONFIG: Record<string, { label: string; color: "success" | "warning" | "error" | "info" | "default" }> = {
  PENDENT: { label: "Pendente", color: "warning" },
  PENDING: { label: "Pendente", color: "warning" },
  PAID: { label: "Pago", color: "success" },
  APPROVED: { label: "Aprovado", color: "success" },
  CANCELED: { label: "Cancelado", color: "error" },
  REFUNDED: { label: "Reembolsado", color: "info" },
  SENT: { label: "Enviado", color: "info" },
  PARTIALLY_PAID: { label: "Parcialmente pago", color: "warning" },
  LATE: { label: "Atrasado", color: "error" },
  ABANDONED: { label: "Abandonado", color: "default" },
  REFUND: { label: "Reembolso", color: "info" },
  CHARGEBACK: { label: "Chargeback", color: "error" },
  PRE_AUTHORIZED: { label: "Pré-autorizado", color: "info" },
  DISPUTE: { label: "Disputa", color: "error" },
  DISPUTE_ALERT: { label: "Alerta de disputa", color: "warning" },
};

const OrderDetails = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;

    orderApi
      .findOne(orderId)
      .then((data) => setOrder(data))
      .catch(() => setError("Erro ao carregar os detalhes do pedido."))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <Box sx={{ overflowX: "hidden" }}>
        <ClientNavbar />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ overflowX: "hidden" }}>
        <ClientNavbar />
        <Box sx={styles.container}>
          <Alert severity="error">{error || "Pedido não encontrado."}</Alert>
        </Box>
      </Box>
    );
  }

  const statusCfg = STATUS_CONFIG[order.status] || { label: order.status, color: "info" };

  return (
    <Box sx={{ overflowX: "hidden" }}>
      <ClientNavbar />
      <Box sx={backButtonContainer}>
        <BackPageButton />
      </Box>

      <Box sx={{ ...sharedStyles.clientMainContainer }}>
        <Box sx={styles.header}>
          <Typography variant="h5" fontWeight="bold">
            Pedido realizado
          </Typography>
          <Chip label={statusCfg.label} color={statusCfg.color} />
        </Box>

        <Box sx={styles.card}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Resumo do pagamento
          </Typography>

          <Box sx={styles.row}>
            <Typography variant="body2" color="text.secondary">Método</Typography>
            <Typography variant="body2" fontWeight={500}>
              {PAYMENT_LABELS[order.payment] || order.payment}
            </Typography>
          </Box>

          <Box sx={styles.row}>
            <Typography variant="body2" color="text.secondary">Valor</Typography>
            <Typography variant="body2" fontWeight={700} color="success.main">
              R$ {priceFormatter(order.amount ?? 0)}
            </Typography>
          </Box>

          <Box sx={styles.row}>
            <Typography variant="body2" color="text.secondary">Data</Typography>
            <Typography variant="body2" fontWeight={500}>
              {dayjs(order.createdAt).format("DD/MM/YYYY [às] HH:mm")}
            </Typography>
          </Box>

          {order.asaasPaymentId && (
            <Box sx={styles.row}>
              <Typography variant="body2" color="text.secondary">ID Asaas</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.8rem" }}>
                {order.asaasPaymentId}
              </Typography>
            </Box>
          )}
        </Box>

        {order.Cart?.CartItem?.length > 0 && (
          <Box sx={styles.card}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Itens do pedido ({order.Cart.CartItem.length})
            </Typography>

            {order.Cart.CartItem.map((item: any) => (
              <Box key={item.id} sx={{ ...styles.row, py: 1 }}>
                <Typography variant="body2">
                  Agendamento #{item.appointmentId?.slice(0, 8) || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(item.createdAt).format("DD/MM/YYYY")}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {order.pix && (
          <Box sx={{ ...styles.card, textAlign: "center" }}>
            <Typography fontWeight={700} gutterBottom>
              Escaneie o QR Code
            </Typography>

            <Box
              component="img"
              src={order.pix.pixQRCodeImage}
              alt="PIX QR Code"
              sx={{
                width: 240,
                height: 240,
                objectFit: "contain",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 1,
                bgcolor: "background.paper",
                mb: 2,
              }}
            />

            {order.pix.pixCode && (
              <>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Código PIX
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={order.pix.pixCode}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 1.5 }}
                />

                <PrimaryButton
                  label="Copiar código PIX"
                  onClickAction={() => {
                    navigator.clipboard.writeText(order.pix.pixCode);
                  }}
                  icon={<ContentCopyIcon />}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrderDetails;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mt: 4,
    gap: 2,
  } as SxProps,
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    mb: 2,
  } as SxProps,
  card: {
    p: 2,
    bgcolor: "background.paper",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    mb: 2,
    width: "100%",
  } as SxProps,
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    py: 0.5,
  } as SxProps,
};
